import { useId, useState, type ReactElement, type ReactNode, cloneElement } from 'react';
import { cn } from '../lib/cn.js';

export interface TooltipProps {
  content: ReactNode;
  side?: 'top' | 'bottom' | 'left' | 'right';
  children: ReactElement<{ 'aria-describedby'?: string; onMouseEnter?: () => void; onMouseLeave?: () => void; onFocus?: () => void; onBlur?: () => void }>;
}

const sideClass = {
  top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
  bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
  left: 'right-full top-1/2 -translate-y-1/2 mr-2',
  right: 'left-full top-1/2 -translate-y-1/2 ml-2',
} as const;

export function Tooltip({ content, side = 'top', children }: TooltipProps) {
  const id = useId();
  const [open, setOpen] = useState(false);
  // Forward only aria-describedby to the child; mouse + focus events are
  // attached to the wrapper so they still fire when the child is disabled
  // (disabled buttons don't dispatch mouse events themselves).
  const child = cloneElement(children, {
    'aria-describedby': open ? id : undefined,
    onFocus: () => setOpen(true),
    onBlur: () => setOpen(false),
  });
  return (
    <span
      className="relative inline-block"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      {child}
      {open && (
        <span
          id={id}
          role="tooltip"
          className={cn(
            'pointer-events-none absolute z-50 whitespace-nowrap rounded-md bg-bg-surface-3 border border-border-strong px-2.5 py-1.5 text-sm text-text-primary shadow-lg',
            sideClass[side],
          )}
        >
          {content}
        </span>
      )}
    </span>
  );
}
